import { createStoreChunk } from '@core/store'

export type TUploadedAudio = { id: string; slug: string; authorUsername: string }

export type TUploadItemStatus = 'uploading' | 'done' | 'failed'

/** Adoption runs outside React and cannot start the batch itself, so it signals through here. */
export type TUploadHandoverStage = 'none' | 'pending' | 'failed'

export type TUploadItem = {
    fileId: string
    fileName: string
    categoryId: number
    /** Held at 99 until the success ping lands, so 100 always means the API has the file. */
    progress: number
    status: TUploadItemStatus
    audio?: TUploadedAudio
}

export type TUploadState = {
    items: TUploadItem[]
    isUploading: boolean
    /**
     * The subset of `items` the server has confirmed, mirrored into sessionStorage so a full reload
     * on the review screen can pick the batch back up. Anything still transferring is left out: its
     * `File` cannot survive a reload either way, and a half-written entry would restore as an upload
     * that can never finish.
     */
    restorableItems: TUploadItem[]
    /** Not persisted: a restored batch must not come back as a card on the profile. */
    displayedFileIds: string[]
    handover: TUploadHandoverStage
}

export const uploadInitialState: TUploadState = {
    items: [],
    isUploading: false,
    restorableItems: [],
    displayedFileIds: [],
    handover: 'none',
}

// `audio.id` alone is not "done": it is set right after the lightweight create call, before a single
// byte of the file has gone up.
const isFullyUploaded = (item: TUploadItem) => Boolean(item.audio) && item.progress >= 100

const sameItems = (a: TUploadItem[], b: TUploadItem[]) =>
    a.length === b.length && a.every((item, index) => item === b[index])

/**
 * Derived from `items` on every change so the two can never disagree after a rehydrate — a restored
 * list that contradicts the live one leaves the review screen blank instead of recovering. The
 * confirmed set is compared by content, not length: a patch replaces the item object, and an equal
 * count would otherwise leave a stale copy behind. Progress ticks are frequent and every write
 * reaches sessionStorage, so an unchanged set keeps its previous reference.
 */
const withRestorable = (items: TUploadItem[], previous: TUploadItem[]): Partial<TUploadState> => {
    const restorableItems = items.filter(isFullyUploaded)

    return sameItems(restorableItems, previous) ? { items } : { items, restorableItems }
}

type TStartBatchPayload = { items: TUploadItem[]; displayedFileIds: string[] }

const uploadActions = {
    startBatch: ({ items, displayedFileIds }: TStartBatchPayload) => ({
        items,
        isUploading: true,
        restorableItems: [],
        displayedFileIds,
    }),
    patchItem: (
        { fileId, patch }: { fileId: string; patch: Partial<TUploadItem> },
        state: TUploadState,
    ) =>
        withRestorable(
            state.items.map(item => (item.fileId === fileId ? { ...item, ...patch } : item)),
            state.restorableItems,
        ),
    dropItem: (fileId: string, state: TUploadState) => ({
        ...withRestorable(
            state.items.filter(item => item.fileId !== fileId),
            state.restorableItems,
        ),
        displayedFileIds: state.displayedFileIds.filter(id => id !== fileId),
    }),
    hideItem: (fileId: string, state: TUploadState) => ({
        displayedFileIds: state.displayedFileIds.filter(id => id !== fileId),
    }),
    finishBatch: () => ({ isUploading: false }),
    setHandover: (handover: TUploadHandoverStage) => ({ handover }),
}

export const uploadStore = createStoreChunk<TUploadState>('upload')({
    persist: { sessionStorage: ['restorableItems'] },
    initialState: uploadInitialState,
    actions: uploadActions,
})
